<script setup>
import { reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import DispatchScheduleEditor from './DispatchScheduleEditor.vue'
import { dispatchSchedulesApi } from '@/api/dispatchSchedules.js'
import { materialAssetsApi } from '@/api/index.js'
import { createScheduleController, scheduleState } from '@/utils/dispatchScheduleController.js'
import { scheduleDraft, scheduleLabel, phaseLabel } from '@/utils/dispatchScheduleForm.js'

const props = defineProps({ kind: String, tenantId: String, project: Object, assignments: Array })
const emit = defineEmits(['saved', 'batch'])
const state = reactive(scheduleState())
const assets = ref([])
const assetError = ref('')
const initial = [props.tenantId, props.project.id]
let disposed = false
let timer = null
const current = () => !disposed && props.tenantId === initial[0] && props.project.id === initial[1]
const controller = createScheduleController({ state, api: dispatchSchedulesApi, tenant: initial[0],
  project: initial[1], kind: props.kind, current, saved: receipt => emit('saved', receipt) })
async function loadAssets() {
  if (props.kind !== 'once' || !current()) return
  try {
    const rows = await materialAssetsApi.list(initial[0], initial[1], { statuses: 'ready' })
    if (current()) { assets.value = rows.filter(row => row.mime_type?.startsWith('image/')); assetError.value = '' }
  } catch (error) { if (current()) assetError.value = error?.message || '图片列表暂时无法读取。' }
}
async function refresh() { await controller.read(); await loadAssets() }
function edit(rule = null) {
  if (state.writing || state.pending || state.editorOpen) return
  state.source = rule
  state.editorOpen = true
  state.saved = ''
  state.writeError = ''
  state.latestSource = null
}
function save(draft) { return controller.save(draft, state.source) }
function toggle(rule) { return controller.save({ ...scheduleDraft(rule.kind, rule), enabled: !rule.enabled }, rule) }
function useLatest() {
  if (!state.source || !state.latestSource || state.pending) return
  state.source.revision = state.latestSource.revision
  state.latestSource = null
  state.writeError = ''
  state.saved = '最新版本已核对，本次输入保留，请确认后保存。'
}
function when(value, timezone) {
  if (!value) return '暂无下一次'
  return new Intl.DateTimeFormat('zh-CN', { timeZone: timezone, month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
}
function remedy(occurrence) {
  const code = occurrence?.error_code || ''
  if (code === 'NO_ACTIVE_BINDINGS') return '目前没有有效接收手机，绑定手机后系统会自动继续。'
  if (code === 'NO_ACTIVE_MANAGER') return '项目执行账号暂时不可用，恢复有效项目权限后自动继续。'
  if (code === 'SCHEDULE_TWO_USABLE_MATERIALS_REQUIRED') return '素材库需要两张可用图片及说明，补齐后自动继续。'
  if (/MATERIAL|DESCRIPTION|FILE|FROZEN/.test(code)) return '请核对所选图片、说明版本与使用授权；本次原选择已保留。'
  return occurrence?.error_message || ''
}
onMounted(async () => {
  await refresh()
  if (current()) timer = window.setInterval(() => {
    if (!state.writing && !state.reading && document.visibilityState === 'visible') controller.read()
  }, 30000)
})
onBeforeUnmount(() => { disposed = true; controller.dispose(); if (timer) window.clearInterval(timer) })
</script>

<template>
  <section class="dispatch-schedules" :aria-label="kind === 'periodic' ? '周期自动下发' : '预设下发'">
    <header><div><h2>{{ kind === 'periodic' ? '周期自动下发' : '预设下发' }}</h2>
      <p>{{ kind === 'periodic' ? '可新增多条每天、每周或每月日程。每次由系统随机选择两张素材。' : '指定日期、小时和分钟，可预选两张图片，也可以交给系统选择。' }}</p>
    </div><button :disabled="state.writing || Boolean(state.pending) || state.editorOpen" @click="edit()">{{ kind === 'periodic' ? '新增周期日程' : '新增预设下发' }}</button></header>
    <p v-if="state.saved" class="success" role="status">{{ state.saved }}</p>
    <p v-if="state.writeError" class="error" role="alert">{{ state.writeError }}</p>
    <div v-if="state.pending" class="notice"><p>保存结果尚未确认，原请求与输入均已保留。</p><button :disabled="state.writing" @click="controller.retryPending">按原请求重试</button><button :disabled="state.reading || state.writing" @click="controller.read">查询保存结果</button></div>
    <div v-if="state.latestSource" class="notice"><p>云端已更新：{{ state.latestSource.name || '未命名日程' }} · {{ scheduleLabel(state.latestSource) }}。本次输入仍保留。</p><button @click="useLatest">保留输入，核对最新版本</button></div>
    <p v-if="state.readError" class="error" role="status">{{ state.readError }} 已保存日程仍保留。</p>
    <p v-if="assetError" class="error">{{ assetError }} <button @click="loadAssets">重新读取图片</button></p>
    <p v-if="!state.schedulerEnabled && !state.reading && !state.readError" class="notice">新日程自动执行尚未启用，已保存的设置会保留。</p>
    <DispatchScheduleEditor v-if="state.editorOpen" :kind="kind" :source="state.source" :assets="assets"
      :assignments="assignments" :timezone="project.metadata?.timezone || 'Asia/Shanghai'" :busy="state.writing" :pending="Boolean(state.pending)" :field-errors="state.fieldErrors"
      @submit="save" @cancel="state.editorOpen = false" />
    <p v-if="!state.items.length && !state.reading" class="empty">{{ kind === 'periodic' ? '还没有周期日程。' : '还没有预设下发。' }}</p>
    <article v-for="rule in state.items" :key="rule.id" class="schedule-row">
      <div class="row-title"><h3>{{ rule.name || (kind === 'periodic' ? '周期日程' : '预设下发') }}</h3>
        <span>{{ !rule.enabled ? '已暂停' : (rule.kind === 'once' && !rule.next_release_at ? '本次已安排' : '已启用') }}</span></div>
      <p class="time">{{ scheduleLabel(rule) }}</p>
      <p>{{ rule.selection_mode === 'explicit_two' ? '已预选两张素材' : '每次由系统选择两张可用素材' }} · {{ rule.target_scope === 'exact_targets' ? `指定 ${rule.targets.length} 台手机` : '执行时所有已绑定手机' }}</p>
      <p v-if="rule.enabled && rule.next_release_at">下次下发：{{ when(rule.next_release_at, rule.timezone) }} · 提前准备：{{ when(rule.next_prepare_at, rule.timezone) }}</p>
      <div v-if="rule.last_occurrence" class="occurrence">
        <strong>{{ phaseLabel(rule.last_occurrence.phase) }}</strong>
        <p>计划下发：{{ when(rule.last_occurrence.planned_at, rule.timezone) }}</p>
        <p>内容就绪 {{ rule.last_occurrence.ready_count }}/{{ rule.last_occurrence.selected_count }} · 手机接收 {{ rule.last_occurrence.received_count }}/{{ rule.last_occurrence.selected_count }} · 图片生成 {{ rule.last_occurrence.generated_count }}/{{ rule.last_occurrence.selected_count }}</p>
        <p v-if="remedy(rule.last_occurrence)" class="error">{{ remedy(rule.last_occurrence) }}</p>
        <button v-if="rule.last_occurrence.dispatch_batch_id" @click="emit('batch', rule.last_occurrence)">查看本批任务</button>
      </div>
      <footer><button :disabled="state.writing || Boolean(state.pending) || state.editorOpen" @click="edit(rule)">编辑</button>
        <button :disabled="state.writing || Boolean(state.pending) || state.editorOpen || (rule.kind === 'once' && !rule.next_release_at)" @click="toggle(rule)">{{ rule.enabled ? '暂停日程' : '恢复日程' }}</button>
      </footer>
    </article>
    <footer><button :disabled="state.reading || state.writing" @click="refresh">{{ state.reading ? '正在读取…' : '重新读取日程' }}</button>
      <small>内容就绪、手机接收和图片生成分别记录。暂停日程停止未来触发。</small></footer>
  </section>
</template>

<style scoped>
.dispatch-schedules { padding: 24px; border: 1px solid #d3e4e6; border-radius: 18px; background: #fff; }
header, .row-title, footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
h2, h3 { margin: 0; color: #173a44; }
p { color: #617f88; line-height: 1.6; overflow-wrap: anywhere; }
button { flex-shrink: 0; min-height: 38px; padding: 8px 14px; border: 1px solid #8bbeba; border-radius: 9px; color: #08746d; background: #eff9f8; font: inherit; font-weight: 800; }
header > button { color: #fff; background: #087c73; }
button:disabled { opacity: .55; }
.schedule-row { margin: 16px 0; padding: 18px; border: 1px solid #d3e4e6; border-radius: 13px; }
.row-title span, .time { color: #087c73; font-weight: 800; }
.occurrence { padding: 12px; border-radius: 10px; background: #f0f8f8; }
.error { color: #a3342c; }
.success { color: #247348; }
.notice { padding: 10px; border-radius: 9px; color: #875810; background: #fff8e5; }
small { color: #6f858d; }
@media (max-width: 700px) { .dispatch-schedules { padding: 16px; } header > button { width: 100%; } .schedule-row { padding: 12px; } }
</style>
